import { pinsApi } from '@/services/pinsApi'

const state = {
  pins: [],
  currentPin: null,
  accessData: null,
  loading: false,
  error: null
}

const getters = {
  activePins: (state) => {
    return state.pins.filter(pin => new Date(pin.expires_at) > new Date())
  },
  expiredPins: (state) => {
    return state.pins.filter(pin => new Date(pin.expires_at) <= new Date())
  },
  isLoading: (state) => state.loading,
  getError: (state) => state.error,
  getCurrentPin: (state) => state.currentPin,
  getAccessData: (state) => {
    // Validate session is still active and not expired
    if (!state.accessData || !state.accessData._sessionValid) {
      return null
    }
    
    // Check if PIN has expired
    if (state.accessData.expires_at && new Date(state.accessData.expires_at) <= new Date()) {
      return null
    }
    
    return state.accessData
  },
  getAllPins: (state) => state.pins
}

const mutations = {
  SET_LOADING(state, loading) {
    state.loading = loading
  },
  SET_ERROR(state, error) {
    state.error = error
  },
  SET_PINS(state, pins) {
    state.pins = pins
  },
  ADD_PIN(state, pin) {
    state.pins.unshift(pin)
  },
  REMOVE_PIN(state, pinId) {
    state.pins = state.pins.filter(pin => pin.id !== pinId)
  },
  SET_CURRENT_PIN(state, pin) {
    state.currentPin = pin
  },
  SET_ACCESS_DATA(state, data) {
    state.accessData = {
      ...data,
      pin_code: data.pin_code || data.code, // Ensure pin_code is always available
      group_id: data.group_id,
      group_name: data.group_name,
      expires_at: data.expires_at,
      time_limit: data.time_limit,
      extra_time: data.extra_time,
      _sessionStarted: new Date().toISOString(),
      _sessionValid: true
    }
    
    // Persist to localStorage for browser refresh scenarios
    try {
      localStorage.setItem('pins_accessData', JSON.stringify(state.accessData))
    } catch (error) {
      console.warn('Failed to persist access data to localStorage:', error)
    }
  },
  CLEAR_ERROR(state) {
    state.error = null
  },
  CLEAR_ACCESS_DATA(state) {
    state.accessData = null
    state.currentPin = null
    
    // Clear from localStorage as well
    try {
      localStorage.removeItem('pins_accessData')
    } catch (error) {
      console.warn('Failed to clear access data from localStorage:', error)
    }
  },
  
  RESTORE_ACCESS_DATA(state, data) {
    // Restore access data from localStorage (e.g., after browser refresh)
    if (data && data._sessionValid) {
      state.accessData = data
    }
  }
}

const actions = {
  async fetchPins({ commit }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    
    try {
      const response = await pinsApi.listPins()
      
      if (response.success) {
        commit('SET_PINS', response.data)
        return { success: true, data: response.data }
      } else {
        commit('SET_ERROR', response.message)
        return { success: false, error: response.message }
      }
    } catch (error) {
      const errorMessage = error.message || 'Error loading PINs'
      commit('SET_ERROR', errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async generatePin({ commit }, pinData) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    
    try {
      const response = await pinsApi.createPin(pinData)
      
      if (response.success) {
        commit('ADD_PIN', response.data)
        return { success: true, data: response.data }
      } else {
        commit('SET_ERROR', response.message)
        return { success: false, error: response.message }
      }
    } catch (error) {
      const errorMessage = error.message || 'Error generating PIN'
      commit('SET_ERROR', errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async validatePin({ commit }, code) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    
    try {
      const response = await pinsApi.validatePin(code)
      
      if (response.success) {
        commit('SET_CURRENT_PIN', response.data)
        return { success: true, data: response.data }
      } else {
        commit('SET_ERROR', response.message)
        return { success: false, error: response.message }
      }
    } catch (error) {
      const errorMessage = error.message || 'PIN validation error'
      commit('SET_ERROR', errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async accessWithPin({ commit }, code) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    
    try {
      const response = await pinsApi.accessWithPin(code)
      
      if (response.success) {
        commit('SET_ACCESS_DATA', response.data)
        return { success: true, data: response.data }
      } else {
        commit('SET_ERROR', response.message)
        return { success: false, error: response.message }
      }
    } catch (error) {
      const errorMessage = error.message || 'Access error'
      commit('SET_ERROR', errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async deletePin({ commit }, pinId) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    
    try {
      const response = await pinsApi.deletePin(pinId)
      
      if (response.success) {
        commit('REMOVE_PIN', pinId)
        return { success: true }
      } else {
        commit('SET_ERROR', response.message)
        return { success: false, error: response.message }
      }
    } catch (error) {
      const errorMessage = error.message || 'Error deleting PIN'
      commit('SET_ERROR', errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      commit('SET_LOADING', false)
    }
  },

  clearError({ commit }) {
    commit('CLEAR_ERROR')
  },

  clearAccessData({ commit }) {
    commit('CLEAR_ACCESS_DATA')
  },
  
  // Action to set access data when student enters a valid PIN
  setAccessData({ commit }, data) {
    console.log('Setting access data:', data)
    commit('SET_ACCESS_DATA', data)
    return { success: true }
  },
  
  // Action to restore access data from localStorage (e.g., after page refresh)
  restoreAccessData({ commit }) {
    try {
      const stored = localStorage.getItem('pins_accessData')
      if (stored) {
        const data = JSON.parse(stored)
        
        // Validate the data is still valid (not expired)
        if (data.expires_at && new Date(data.expires_at) <= new Date()) {
          console.log('Stored access data expired, clearing')
          localStorage.removeItem('pins_accessData')
          return { success: false, error: 'Access data expired' }
        }
        
        console.log('Restoring access data from localStorage:', data)
        commit('RESTORE_ACCESS_DATA', data)
        return { success: true, data }
      }
    } catch (error) {
      console.warn('Failed to restore access data from localStorage:', error)
      localStorage.removeItem('pins_accessData')
    }
    return { success: false, error: 'No valid stored access data' }
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}